// src/pages/DestinationExperience.jsx
import React, { useState, useRef, useEffect, useMemo } from "react";
import { useCart } from "../cart/CartContext";
import { useAuth } from "../auth/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import {
  FaBars,
  FaTimes,
  FaShoppingCart,
  FaHome,
  FaChevronDown,
  FaUserCircle,
  FaCalendarAlt,
  FaTimesCircle,
  FaMapMarkerAlt,
  FaStar,
  FaHeart,
  FaRegHeart,
  FaFilter,
  FaSearch,
  FaArrowRight,
  FaUsers,
  FaAward,
  FaHeadset,
  FaShieldAlt,
  FaChevronLeft,
  FaChevronRight,
  FaChevronUp,  // ADD THIS LINE
} from "react-icons/fa";
import "../styles/DestinationExperience.css";

const destinationServices = [
  { to: "/wedding", label: "Wedding Planning" },
  { to: "/design", label: "Design & Decoration" },
  { to: "/destination", label: "Destination & Venues" },
  { to: "/entertainment", label: "Entertainment" },
  { to: "/others", label: "Custom Events" },
];

const quickCategories = [
  { icon: "🏰", label: "Palaces", tag: "Palace" },
  { icon: "🏖️", label: "Beaches", tag: "Beach" },
  { icon: "⛰️", label: "Hills", tag: "Hill" },
  { icon: "🌊", label: "Backwaters", tag: "Backwater" },
  { icon: "🌵", label: "Desert", tag: "Desert" },
  { icon: "🌳", label: "Gardens", tag: "Garden" },
];

const stats = [
  { icon: <FaUsers />, value: "500+", label: "Happy Couples" },
  { icon: <FaAward />, value: "50+", label: "Awards Won" },
  { icon: <FaHeadset />, value: "24/7", label: "Support" },
  { icon: <FaShieldAlt />, value: "100%", label: "Secure Booking" },
];

/* -------------------- helpers for availability (UPDATED) -------------------- */
const toISODate = (val) => {
  if (!val) return "";
  if (val instanceof Date && !isNaN(val)) {
    const y = val.getFullYear();
    const m = String(val.getMonth() + 1).padStart(2, "0");
    const d = String(val.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  const s = String(val);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const m = /^(\d{2})[/.-](\d{2})[/.-](\d{4})$/.exec(s);
  if (m) {
    const [, dd, mm, yyyy] = m;
    return `${yyyy}-${mm}-${dd}`;
  }
  const tryDate = new Date(s);
  if (!isNaN(tryDate)) return toISODate(tryDate);
  return s.slice(0, 10);
};

const inRange = (d, from, to) => d >= toISODate(from) && d <= toISODate(to);

function isAvailableOn(dest, d) {
  if (!d) return true;
  const day = toISODate(d);

  const single = Array.isArray(dest.unavailableDates)
    ? dest.unavailableDates.map(toISODate)
    : [];
  if (single.includes(day)) return false;

  const blocks = Array.isArray(dest.unavailableBlocks) ? dest.unavailableBlocks : [];
  for (const b of blocks) {
    if (b?.from && b?.to && inRange(day, b.from, b.to)) return false;
  }
  return true;
}

function DestinationExperience() {
  const { addToCart, cart } = useCart();
  const { user } = useAuth();

  // Navbar state
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const dropdownTimer = useRef(null);
  const navigate = useNavigate();

  const handleProfileClick = (e) => {
    e.preventDefault();
    if (!user) {
      navigate("/login");
    } else {
      navigate("/profile");
    }
  };

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [searchDate, setSearchDate] = useState("");
  const [cardDate, setCardDate] = useState({});
  const [favorites, setFavorites] = useState(new Set());
  const [showFilters, setShowFilters] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const [priceRange, setPriceRange] = useState([0, 0]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [selectedDest, setSelectedDest] = useState(null); // modal
  const [modalImgIdx, setModalImgIdx] = useState(0); // gallery index

  // CMS data
  const [cms, setCms] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState("");
  const [heroLoaded, setHeroLoaded] = useState(false);

  // Load CMS data
  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setLoadErr("");

    fetch(`/content/destinations.json?ts=${Date.now()}`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => {
        if (isMounted) {
          setCms(data);
          setLoading(false);
          setTimeout(() => setHeroLoaded(true), 100);
        }
      })
      .catch(() => {
        if (isMounted) {
          setLoadErr("Failed to load content. Please open /admin and save once.");
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  // Derive max price dynamically from data
  const maxPrice = useMemo(() => {
    const data = Array.isArray(cms?.destinations) ? cms.destinations : [];
    return data.length > 0 ? Math.max(...data.map(d => d.price || 0)) : 5000000;
  }, [cms?.destinations]);

  // Set priceRange max once data loads
  useEffect(() => {
    if (maxPrice > 0) setPriceRange([0, maxPrice]);
  }, [maxPrice]);
  const allTags = useMemo(() => {
    const data = Array.isArray(cms?.destinations) ? cms.destinations : [];
    const tags = new Set();
    data.forEach(d => {
      if (Array.isArray(d.tags)) d.tags.forEach(t => tags.add(t));
    });
    return Array.from(tags).sort();
  }, [cms?.destinations]);

  // FILTERED DATA - defined BEFORE the useEffect that uses it
  const filteredData = useMemo(() => {
    const data = Array.isArray(cms?.destinations) ? cms.destinations : [];
    const t = searchTerm.trim().toLowerCase();
    
    let list = data.filter((d) => {
      const tags = Array.isArray(d.tags) ? d.tags : [];
      const hay = [d.name, d.city, ...tags].join(" ").toLowerCase();
      const matchesText = !t || hay.includes(t);
      const okOnDate = isAvailableOn(d, searchDate);
      const matchesTags = selectedTags.length === 0 || selectedTags.every(tag => tags.includes(tag));
      const matchesCategory = !activeCategory || tags.includes(activeCategory);
      const matchesPrice = (d.price || 0) >= priceRange[0] && (d.price || 0) <= priceRange[1];
      
      return matchesText && (searchDate ? okOnDate : true) && matchesTags && matchesCategory && matchesPrice;
    });

    if (sortOrder === "low-high") list = list.sort((a, b) => (a.price || 0) - (b.price || 0));
    if (sortOrder === "high-low") list = list.sort((a, b) => (b.price || 0) - (a.price || 0));
    if (sortOrder === "rating") list = list.sort((a, b) => (b.rating || 0) - (a.rating || 0));
    
    return list;
  }, [cms?.destinations, searchTerm, sortOrder, searchDate, selectedTags, activeCategory, priceRange]);

  // Intersection Observer useEffect - MOVED HERE after filteredData definition
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("active")),
      { threshold: 0.15 }
    );
    els.forEach((el) => observer.observe(el));
    
    return () => observer.disconnect();
  }, [filteredData]);

  const toggleFavorite = (id) => {
    setFavorites(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleTag = (tag) => {
    setSelectedTags(prev => 
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  const handleCategoryClick = (tag) => {
    setActiveCategory(prev => prev === tag ? null : tag);
  };

  function handleAddPackage(dest) {
    if (!user) {
      alert("Please login to add items to cart");
      return;
    }
    const selected = cardDate[dest.id] || searchDate || "";
    const available = isAvailableOn(dest, selected);
    if (!selected) {
      alert("Please select a booking date first.");
      return;
    }
    if (!available) {
      alert("This venue is not available on the selected date.");
      return;
    }
    addToCart({
      id: `${dest.id}@${selected}`,
      name: dest.name,
      price: Number(dest.price || 0),
      image: dest.image,
      description: dest.description || "",
      bookingDate: selected,
      city: dest.city,
    });
  }

  const clearAllFilters = () => {
    setSearchTerm("");
    setSearchDate("");
    setSelectedTags([]);
    setActiveCategory(null);
    setPriceRange([0, maxPrice]);
    setSortOrder("");
  };

  // Get featured destinations for marquee
  const featuredDestinations = useMemo(() => {
    const data = Array.isArray(cms?.destinations) ? cms.destinations : [];
    return data.slice(0, 6);
  }, [cms?.destinations]);

  return (
    <div className="destination-experience">
      {/* Navbar - DO NOT TOUCH */}
      <header className="dest-navbar">
        <div className="dest-navbar-container">
          <div className="dest-navbar-brand">
            <Link to="/">Parivar</Link>
          </div>

          <nav className="dest-center-nav">
            <Link to="/" className="dest-nav-link">
              <FaHome /> Home
            </Link>

            <div
              className="dest-dropdown"
              onMouseEnter={() => {
                clearTimeout(dropdownTimer.current);
                setIsDropdownOpen(true);
              }}
              onMouseLeave={() => {
                dropdownTimer.current = setTimeout(() => setIsDropdownOpen(false), 300);
              }}
            >
              <button className="dest-dropdown-btn">
                Services <FaChevronDown />
              </button>
              {isDropdownOpen && (
                <div className="dest-dropdown-menu">
                  {destinationServices.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      className="dest-dropdown-link"
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </nav>

          <div className="dest-nav-right">
            <a href="/profile" onClick={handleProfileClick} className="dest-cart-link" aria-label="Profile">
              <FaUserCircle size={18} />
            </a>

            <Link to="/cart" className="dest-cart-link" aria-label="Cart">
              <FaShoppingCart size={18} />
              {cart.length > 0 && <span className="dest-cart-badge">{cart.length}</span>}
            </Link>
          </div>

          <div className="dest-mobile-menu-icon" onClick={() => setIsMobileNavOpen(true)}>
            <FaBars size={22} />
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {isMobileNavOpen && (
        <div className="dest-sidebar-overlay" onClick={() => setIsMobileNavOpen(false)}>
          <div className="dest-sidebar" onClick={(e) => e.stopPropagation()}>
            <div className="dest-sidebar-header">
              <span>Menu</span>
              <FaTimes onClick={() => setIsMobileNavOpen(false)} />
            </div>

            <div className="dest-sidebar-link">
              <Link to="/" onClick={() => setIsMobileNavOpen(false)}>
                <FaHome /> Home
              </Link>
            </div>

            <div className="dest-sidebar-link" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
              <FaChevronDown /> Services
            </div>
            {isDropdownOpen && (
              <div className="dest-sidebar-submenu">
                {destinationServices.map((link) => (
                  <div key={link.to} className="dest-sidebar-submenu-item" onClick={() => setIsMobileNavOpen(false)}>
                    <Link to={link.to}>{link.label}</Link>
                  </div>
                ))}
              </div>
            )}

            <div className="dest-sidebar-link">
              <a href="/profile" onClick={(e) => {
                e.preventDefault();
                if (!user) {
                  navigate("/login");
                } else {
                  navigate("/profile");
                }
                setIsMobileNavOpen(false);
              }}>
                <FaUserCircle /> Profile
              </a>
            </div>

            <div className="dest-sidebar-link">
              <Link to="/cart" onClick={() => setIsMobileNavOpen(false)}>
                <FaShoppingCart /> Cart
                {cart.length > 0 && <span className="dest-cart-badge">{cart.length}</span>}
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* NEW SPLIT HERO SECTION */}
      <section className={`dest-hero-split ${heroLoaded ? 'loaded' : ''}`}>
        <div className="dest-hero-left">
          <div className="dest-hero-badge">
            <span className="dest-hero-badge-icon">✦</span>
            <span>India's #1 Wedding Destination Platform</span>
          </div>
          
          <h1 className="dest-hero-title">
            Find Your <span className="highlight">Dream</span> Wedding Venue
          </h1>
          
          <p className="dest-hero-subtitle">
            Discover 500+ handpicked palaces, beaches, resorts, and heritage properties across India. 
            From royal Rajasthan to serene Kerala backwaters.
          </p>

          {/* Search Bar */}
          <div className="dest-hero-search-box">
            <div className="dest-hero-search-field">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search destinations, cities..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="dest-hero-search-divider" />
            <div className="dest-hero-search-field">
              <FaCalendarAlt className="calendar-icon" />
              <input
                type="date"
                value={searchDate}
                onChange={(e) => setSearchDate(toISODate(e.target.value))}
                placeholder="Wedding date"
              />
            </div>
            <button className="dest-hero-search-btn">
              <FaArrowRight />
            </button>
          </div>

          {/* Quick Category Pills */}
          <div className="dest-hero-categories">
            <span className="dest-hero-categories-label">Popular:</span>
            {quickCategories.map((cat) => (
              <button
                key={cat.tag}
                className={`dest-category-pill ${activeCategory === cat.tag ? 'active' : ''}`}
                onClick={() => handleCategoryClick(cat.tag)}
              >
                <span>{cat.icon}</span>
                {cat.label}
              </button>
            ))}
          </div>

          {/* Stats */}
          <div className="dest-hero-stats">
            {stats.map((stat, idx) => (
              <div key={idx} className="dest-stat-item">
                <div className="dest-stat-icon">{stat.icon}</div>
                <div className="dest-stat-value">{stat.value}</div>
                <div className="dest-stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="dest-hero-right">
          <div className="dest-hero-image-grid">
            <div className="dest-hero-img dest-hero-img-1">
              <img src="https://images1.wionews.com/images/wion/original/2023/12/21/1703140013701_c4e61029ef0955e1a005e9ca80eaaaa4.jpg?imwidth=400&imheight=400" alt="Wedding venue" />
            </div>
            <div className="dest-hero-img dest-hero-img-2">
              <img src="https://weddingphotographybysf.com/wp-content/uploads/2024/11/Alibag-Destination-Wedding-Location-1024x683.webp" alt="Beach wedding" />
            </div>
            <div className="dest-hero-img dest-hero-img-3">
              <img src="https://www.varmalla.com/wp-content/uploads/2023/11/103587-weddingnama3.jpeg" alt="Palace wedding" />
            </div>
            <div className="dest-hero-img dest-hero-img-4">
              <img src="https://images.unsplash.com/photo-1596394516093-501ba68a0ba6?w=600&q=80" alt="Resort wedding" />
            </div>
          </div>
          <div className="dest-hero-floating-card">
            <div className="dest-floating-card-stars">
              <FaStar /> <FaStar /> <FaStar /> <FaStar /> <FaStar />
            </div>
            <p>"The most magical day of our lives!"</p>
            <div className="dest-floating-card-author">
              <img src="https://randomuser.me/api/portraits/women/44.jpg" alt="User" />
              <span>Priya & Lakshay</span>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED DESTINATIONS MARQUEE */}
      <section className="dest-marquee-section reveal">
        <div className="dest-marquee-header">
          <h3>Featured Destinations</h3>
          <div className="dest-marquee-nav">
            <button><FaChevronLeft /></button>
            <button><FaChevronRight /></button>
          </div>
        </div>
        <div className="dest-marquee">
          <div className="dest-marquee-track">
            {[...featuredDestinations, ...featuredDestinations].map((dest, idx) => (
              <div key={idx} className="dest-marquee-item">
                <img src={dest.image} alt={dest.name} />
                <div className="dest-marquee-overlay">
                  <span className="dest-marquee-name">{dest.name}</span>
                  <span className="dest-marquee-city">{dest.city}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MAIN CONTENT AREA */}
      <div className="dest-main-content">
        {/* Sticky Sidebar Controls */}
        <aside className="dest-sidebar-controls reveal">
          <div className="dest-sidebar-sticky">
            <div className="dest-sidebar-header">
              <h3>Filters</h3>
              {(selectedTags.length > 0 || activeCategory || priceRange[1] < maxPrice) && (
                <button className="dest-clear-btn" onClick={clearAllFilters}>
                  Clear all
                </button>
              )}
            </div>

            {/* Price Range */}
            <div className="dest-filter-section">
              <label>Price Range</label>
              <div className="dest-price-slider">
                <input 
                  type="range" 
                  min="0" 
                  max={maxPrice} 
                  step={Math.ceil(maxPrice / 100)}
                  value={priceRange[1]}
                  onChange={(e) => setPriceRange([0, Number(e.target.value)])}
                />
                <div className="dest-price-labels">
                  <span>₹0</span>
                  <span>₹{(priceRange[1] / 100000).toFixed(1)}L</span>
                </div>
              </div>
            </div>

            {/* Categories */}
            <div className="dest-filter-section">
              <label>Categories</label>
              <div className="dest-filter-tags">
                {allTags.slice(0, 8).map(tag => (
                  <button
                    key={tag}
                    className={`dest-filter-tag ${selectedTags.includes(tag) ? 'active' : ''}`}
                    onClick={() => toggleTag(tag)}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>

            {/* Rating Filter */}
            <div className="dest-filter-section">
              <label>Rating</label>
              <div className="dest-rating-options">
                {[5, 4, 3].map(rating => (
                  <button key={rating} className="dest-rating-option">
                    <span className="dest-rating-stars">
                      {[...Array(rating)].map((_, i) => <FaStar key={i} />)}
                    </span>
                    <span>& Up</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </aside>

        {/* Results Area */}
        <div className="dest-results-area">
          {/* Top Bar */}
          <div className="dest-results-topbar reveal">
            <div className="dest-results-info">
              <h2>{activeCategory || 'All Destinations'}</h2>
              <span className="dest-results-count">{filteredData.length} venues found</span>
            </div>
            <div className="dest-results-actions">
              <button 
                className={`dest-filter-toggle-btn ${showFilters ? 'active' : ''}`}
                onClick={() => setShowFilters(!showFilters)}
              >
                <FaFilter /> Filters
              </button>
              <select 
                value={sortOrder} 
                onChange={(e) => setSortOrder(e.target.value)}
                className="dest-sort-select"
              >
                <option value="">Sort by: Recommended</option>
                <option value="low-high">Price: Low to High</option>
                <option value="high-low">Price: High to Low</option>
                <option value="rating">Top Rated</option>
              </select>
            </div>
          </div>

          {/* Mobile Filters Panel */}
          {showFilters && (
            <div className="dest-mobile-filters reveal">
              {/* Same filter content as sidebar */}
            </div>
          )}

          {/* Cards Grid */}
          {!loading && !loadErr && (
            <div className="dest-cards-grid">
              {filteredData.length === 0 ? (
                <div className="dest-empty-state reveal">
                  <div className="dest-empty-illustration">
                    <img src="https://cdn-icons-png.flaticon.com/128/4021/4021678.png" alt="No results" />
                  </div>
                  <h3>No destinations found</h3>
                  <p>Try adjusting your filters or search for something else</p>
                  <button className="dest-empty-cta" onClick={clearAllFilters}>
                    Clear all filters
                  </button>
                </div>
              ) : (
                filteredData.map((dest, index) => {
                  const chosen = cardDate[dest.id] || searchDate || "";
                  const isAvail = isAvailableOn(dest, chosen);
                  const isFav = favorites.has(dest.id);

                  return (
                    <div 
                      key={dest.id} 
                      className={`dest-card-v2 ${!isAvail && chosen ? 'unavailable' : ''}`}
                      style={{ animationDelay: `${index * 0.05}s` }}
                    >
                      <div className="dest-card-v2-image" onClick={() => { setSelectedDest(dest); setModalImgIdx(0); }} style={{ cursor: 'pointer' }}>
                        <img src={dest.image} alt={dest.name} loading="lazy" />
                        
                        {/* Badges */}
                        <div className="dest-card-v2-badges">
                          {dest.rating >= 4.5 && (
                            <span className="dest-badge-trending">🔥 Trending</span>
                          )}
                          {chosen && (
                            <span className={`dest-badge-avail ${isAvail ? 'yes' : 'no'}`}>
                              {isAvail ? '✓ Available' : '✗ Booked'}
                            </span>
                          )}
                        </div>

                        {/* Favorite */}
                        <button 
                          className={`dest-fav-btn-v2 ${isFav ? 'active' : ''}`}
                          onClick={() => toggleFavorite(dest.id)}
                        >
                          {isFav ? <FaHeart /> : <FaRegHeart />}
                        </button>

                        {/* Price */}
                        <div className="dest-card-v2-price">
                          <span>from</span>
                          <strong>₹{(dest.price || 0).toLocaleString()}</strong>
                        </div>
                      </div>

                      <div className="dest-card-v2-content">
                        <div className="dest-card-v2-header" onClick={() => { setSelectedDest(dest); setModalImgIdx(0); }} style={{ cursor: 'pointer' }}>
                          <h3>{dest.name}</h3>
                          <div className="dest-card-v2-location">
                            <FaMapMarkerAlt /> {dest.city}
                          </div>
                        </div>

                        {dest.rating && (
                          <div className="dest-card-v2-rating">
                            <span className="dest-rating-stars">
                              {[...Array(5)].map((_, i) => (
                                <FaStar key={i} className={i < Math.floor(dest.rating) ? 'filled' : ''} />
                              ))}
                            </span>
                            <span className="dest-rating-value">{dest.rating}</span>
                            <span className="dest-rating-count">(128 reviews)</span>
                          </div>
                        )}

                        {Array.isArray(dest.tags) && dest.tags.length > 0 && (
                          <div className="dest-card-v2-tags">
                            {dest.tags.slice(0, 3).map((tag, i) => (
                              <span key={i} className="dest-tag-v2">{tag}</span>
                            ))}
                          </div>
                        )}

                        <p className="dest-card-v2-desc">{dest.description}</p>

                        {/* Date Picker */}
                        <div className="dest-card-v2-date">
                          <div className="dest-date-input-v2">
                            <FaCalendarAlt />
                            <input
                              type="date"
                              value={chosen}
                              onChange={(e) => setCardDate(prev => ({ ...prev, [dest.id]: toISODate(e.target.value) }))}
                            />
                            {chosen && (
                              <button onClick={() => setCardDate(prev => ({ ...prev, [dest.id]: "" }))}>
                                <FaTimesCircle />
                              </button>
                            )}
                          </div>
                        </div>

                        <button
                          onClick={() => handleAddPackage(dest)}
                          disabled={!chosen || !isAvail}
                          className={`dest-card-v2-cta ${(!chosen || !isAvail) ? 'disabled' : ''}`}
                        >
                          {!chosen ? 'Select Date to Book' : !isAvail ? 'Not Available' : 'Add to Cart'}
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>

      {/* Floating Action Button */}
      <button className="dest-fab-v2" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
        <FaChevronUp />
      </button>

      {/* Detail Modal */}
      {selectedDest && (() => {
        const allImages = [selectedDest.image, ...(Array.isArray(selectedDest.gallery) ? selectedDest.gallery : [])].filter(Boolean);
        return (
        <div className="dest-modal-overlay" onClick={() => setSelectedDest(null)}>
          <div className="dest-modal" onClick={e => e.stopPropagation()}>
            <button className="dest-modal-close" onClick={() => setSelectedDest(null)}>
              <FaTimes />
            </button>

            {/* Main image */}
            <div className="dest-modal-image">
              <img src={allImages[modalImgIdx]} alt={selectedDest.name} />
              {allImages.length > 1 && (
                <>
                  <button className="dest-modal-img-nav prev" onClick={() => setModalImgIdx(i => (i - 1 + allImages.length) % allImages.length)}>
                    <FaChevronLeft />
                  </button>
                  <button className="dest-modal-img-nav next" onClick={() => setModalImgIdx(i => (i + 1) % allImages.length)}>
                    <FaChevronRight />
                  </button>
                  <span className="dest-modal-img-counter">{modalImgIdx + 1} / {allImages.length}</span>
                </>
              )}
              <div className="dest-modal-price-badge">
                <span>from</span>
                <strong>₹{(selectedDest.price || 0).toLocaleString()}</strong>
              </div>
            </div>

            {/* Thumbnail strip */}
            {allImages.length > 1 && (
              <div className="dest-modal-thumbs">
                {allImages.map((img, i) => (
                  <img
                    key={i}
                    src={img}
                    alt=""
                    className={`dest-modal-thumb ${i === modalImgIdx ? 'active' : ''}`}
                    onClick={() => setModalImgIdx(i)}
                  />
                ))}
              </div>
            )}

            <div className="dest-modal-body">
              <div className="dest-modal-header">
                <div>
                  <h2>{selectedDest.name}</h2>
                  <p className="dest-modal-location"><FaMapMarkerAlt /> {selectedDest.city}</p>
                </div>
                <span className={`dest-modal-status ${selectedDest.status === 'available' ? 'available' : 'unavailable'}`}>
                  {selectedDest.status === 'available' ? '✓ Available' : '✗ Unavailable'}
                </span>
              </div>

              {selectedDest.rating && (
                <div className="dest-card-v2-rating" style={{ marginBottom: 16 }}>
                  <span className="dest-rating-stars">
                    {[...Array(5)].map((_, i) => (
                      <FaStar key={i} className={i < Math.floor(selectedDest.rating) ? 'filled' : ''} />
                    ))}
                  </span>
                  <span className="dest-rating-value">{selectedDest.rating}</span>
                  <span className="dest-rating-count">(128 reviews)</span>
                </div>
              )}

              {Array.isArray(selectedDest.tags) && selectedDest.tags.length > 0 && (
                <div className="dest-card-v2-tags" style={{ marginBottom: 16 }}>
                  {selectedDest.tags.map((tag, i) => (
                    <span key={i} className="dest-tag-v2">{tag}</span>
                  ))}
                </div>
              )}

              <p className="dest-modal-desc">{selectedDest.description}</p>

              {Array.isArray(selectedDest.unavailableDates) && selectedDest.unavailableDates.length > 0 && (
                <div className="dest-modal-section">
                  <h4>Unavailable Dates</h4>
                  <div className="dest-modal-dates">
                    {selectedDest.unavailableDates.map((d, i) => (
                      <span key={i} className="dest-modal-date-chip">{d}</span>
                    ))}
                  </div>
                </div>
              )}

              {Array.isArray(selectedDest.unavailableBlocks) && selectedDest.unavailableBlocks.length > 0 && (
                <div className="dest-modal-section">
                  <h4>Blocked Periods</h4>
                  <div className="dest-modal-dates">
                    {selectedDest.unavailableBlocks.map((b, i) => (
                      <span key={i} className="dest-modal-date-chip">{b.from} → {b.to}</span>
                    ))}
                  </div>
                </div>
              )}

              <div className="dest-modal-book">
                <div className="dest-date-input-v2">
                  <FaCalendarAlt />
                  <input
                    type="date"
                    value={cardDate[selectedDest.id] || searchDate || ""}
                    onChange={e => setCardDate(prev => ({ ...prev, [selectedDest.id]: toISODate(e.target.value) }))}
                  />
                </div>
                <button
                  className={`dest-card-v2-cta ${(!(cardDate[selectedDest.id] || searchDate) || !isAvailableOn(selectedDest, cardDate[selectedDest.id] || searchDate)) ? 'disabled' : ''}`}
                  onClick={() => { handleAddPackage(selectedDest); setSelectedDest(null); }}
                  disabled={!(cardDate[selectedDest.id] || searchDate) || !isAvailableOn(selectedDest, cardDate[selectedDest.id] || searchDate)}
                >
                  {!(cardDate[selectedDest.id] || searchDate) ? 'Select Date to Book' : !isAvailableOn(selectedDest, cardDate[selectedDest.id] || searchDate) ? 'Not Available' : 'Add to Cart'}
                </button>
              </div>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
}

export default DestinationExperience;